import { supabase } from "./supabase";
import { baixarCsv, montarCsv } from "./csv";

// O IO do Admin Pós — notas, matrículas e as listas de filtro.
//
// ---------------------------------------------------------------------------
// ZERO regra aqui
// ---------------------------------------------------------------------------
// O filtro, a ordenação, o corte e as contagens são do BANCO (000143). Este
// arquivo só tipa e chama, como `parametrosAdmin.ts` faz na aba Parâmetros.
//
// O motivo é escala e não pureza: `pos_tentativas` tem **246.529** linhas e
// `matriculas` **43.077**. Filtrar em JavaScript exigiria trazer tudo — e a fila
// de documentos já mostrou onde isso termina, com a aba saindo em 41.910px.
//
// ---------------------------------------------------------------------------
// O TOTAL vem em cada linha, e não como segunda chamada
// ---------------------------------------------------------------------------
// `total_no_filtro` é o total do FILTRO, não da página. Sem ele a tela não pode
// dizer "50 de 4.312" nem saber se há próxima página — e uma segunda chamada de
// contagem correria o risco de divergir da primeira entre uma e outra.
//
// A leitura da fila de DOCUMENTOS não está aqui: ela mora em `faculdade.ts`
// (`lerFila`), porque é a mesma função que a aba Suporte já usava desde 31/08.
// Duas cópias divergiriam.

export type FiltrosDoAdminPos = {
  cursos: { slug: string; nome: string | null }[];
  turmas: { id: string; curso_slug: string; numero: number | null; matriculados: number }[];
  disciplinas: { id: string; curso_slug: string; nome: string }[];
  situacoes: string[];
  /**
   * As PONTAS de cada período (000206), para os campos de data não convidarem a
   * filtrar o nada.
   *
   * A de notas é sobre `coalesce(finalizada_em, iniciada_em)` — a data que a
   * tela mostra na coluna "Data". Se fosse `iniciada_em`, as duas pontas seriam
   * o MESMO dia: as 246.529 tentativas foram carregadas com 31/08/2026 ali (a
   * data da carga; a origem não trouxe a de início), contra 523 dias distintos
   * em `finalizada_em`.
   */
  periodo_notas: { de: string | null; ate: string | null };
  periodo_matriculas: { de: string | null; ate: string | null };
};

/**
 * As gavetas de prazo dos dois painéis (000206) — o MESMO vocabulário do banco
 * (`pos_gaveta_de_prazo`), e o mesmo nos dois painéis.
 *
 * `estourou` é o prazo MÁXIMO do curso: 18 meses na CGT, 24 nos outros sete, e
 * `sem_prazo` para quem não tem matrícula daquele curso ou cujo curso não tem
 * máximo cadastrado (a Pós Auditoria, cuja ausência é DITA — 000148/000192).
 *
 * **É informação, nunca trava.** A 000144 decidiu que o máximo não impede
 * certificado, porque 73,4% das matrículas da CGT já o estouraram e recusar
 * certificado a 2.811 pessoas que pagaram é o erro irreversível do princípio 1.
 * O rótulo diz "fora do prazo do curso" e não "vencida" por isso: vencida soa
 * como perdeu o direito.
 */
export type GavetaDePrazo = "dentro" | "estourou" | "sem_prazo";
export const GAVETAS_DE_PRAZO: readonly GavetaDePrazo[] = ["dentro", "estourou", "sem_prazo"];
export const ROTULO_GAVETA_DE_PRAZO: Record<GavetaDePrazo, string> = {
  dentro: "Dentro do prazo",
  estourou: "Fora do prazo do curso",
  sem_prazo: "Sem prazo definido",
};
/** Valor que a tela não conhece cai em nulo, que é "sem filtro". */
export const gavetaDaQuery = (v: string | null): GavetaDePrazo | null =>
  (GAVETAS_DE_PRAZO as readonly string[]).includes(v ?? "") ? (v as GavetaDePrazo) : null;

export type NotaDoAdmin = {
  id: string;
  aluno_id: string;
  aluno_nome: string | null;
  aluno_email: string;
  curso_slug: string;
  curso_nome: string | null;
  turma_numero: number | null;
  disciplina_id: string;
  disciplina: string;
  numero: number;
  nota: number | null;
  /**
   * A nota como o Bubble a tinha, quando ela era impossível.
   *
   * São **184** tentativas com nota acima de 10 na origem — defeito do Bubble,
   * confirmado por Victor em 31/08 ("no bubble temos algumas falhas"). A carga
   * guardou o valor original aqui e deixou `nota` nula, em vez de descartar ou
   * de aparar para 10: aparar inventaria uma nota que ninguém deu, e descartar
   * perderia o registro de que houve uma tentativa.
   *
   * A tela da equipe mostra este número quando ele existe, porque é ela que vai
   * decidir o que fazer com esses 184 casos.
   */
  nota_bubble: number | null;
  acertos: number | null;
  situacao: "em_andamento" | "finalizada" | "abandonada";
  /** Nulo quando a tentativa não terminou — ali "aprovada" não é dizível. */
  aprovada: boolean | null;
  iniciada_em: string;
  finalizada_em: string | null;
  total_no_filtro: number;
};

export type MatriculaDoAdmin = {
  id: string;
  aluno_id: string;
  aluno_nome: string | null;
  aluno_email: string;
  aluno_cpf: string | null;
  curso_slug: string;
  curso_nome: string | null;
  turma_id: string | null;
  turma_numero: number | null;
  situacao: string;
  /** `fbc` · `focus` · **nulo**, que quer dizer "não sei" e não FBC. */
  instituicao: string | null;
  inicio: string | null;
  previsao_termino: string | null;
  meses_desde_o_inicio: number | null;
  disciplinas_da_turma: number;
  aprovadas: number;
  /**
   * A gaveta de prazo desta matrícula (000206), da MESMA função que o filtro
   * usa. Sai na linha porque filtrar por "fora do prazo" e a lista não dizer
   * qual está fora é a tela contradizendo o próprio filtro.
   */
  prazo: GavetaDePrazo;
  total_no_filtro: number;
};

export type OrdemDasNotas = "recente" | "nota" | "avaliacao";

export async function lerFiltros(): Promise<FiltrosDoAdminPos | null> {
  const { data, error } = await supabase.rpc("pos_admin_filtros");
  if (error) throw error;
  // A função devolve NULL para quem não é equipe, em vez de estourar: a tela do
  // painel decide o que mostrar, e um 42501 no carregamento inicial derrubaria a
  // aba inteira antes de ela poder dizer o motivo.
  return (data ?? null) as FiltrosDoAdminPos | null;
}

export async function lerNotas(f: {
  curso?: string | null;
  turma?: string | null;
  disciplina?: string | null;
  busca?: string | null;
  ordem?: OrdemDasNotas;
  limite?: number;
  offset?: number;
  /** `aaaa-mm-dd` — o período sobre a data que a tela mostra (000206). */
  de?: string | null;
  ate?: string | null;
  /** O prazo da MATRÍCULA do aluno no curso: prova não tem prazo (000206). */
  prazo?: GavetaDePrazo | null;
}): Promise<NotaDoAdmin[]> {
  const { data, error } = await supabase.rpc("pos_admin_notas", {
    p_curso: f.curso ?? null,
    p_turma: f.turma ?? null,
    p_disciplina: f.disciplina ?? null,
    p_busca: f.busca?.trim() ? f.busca.trim() : null,
    p_ordem: f.ordem ?? "recente",
    p_limite: f.limite ?? 50,
    p_offset: f.offset ?? 0,
    p_de: f.de ?? null,
    p_ate: f.ate ?? null,
    p_prazo: f.prazo ?? null,
  });
  if (error) throw error;
  return (data ?? []) as NotaDoAdmin[];
}

export async function lerMatriculas(f: {
  curso?: string | null;
  turma?: string | null;
  situacao?: string | null;
  instituicao?: string | null;
  busca?: string | null;
  limite?: number;
  offset?: number;
  /** `aaaa-mm-dd` — o período sobre o INÍCIO da matrícula (000206). */
  de?: string | null;
  ate?: string | null;
  prazo?: GavetaDePrazo | null;
}): Promise<MatriculaDoAdmin[]> {
  const { data, error } = await supabase.rpc("pos_admin_matriculas", {
    p_curso: f.curso ?? null,
    p_turma: f.turma ?? null,
    p_situacao: f.situacao ?? null,
    p_instituicao: f.instituicao ?? null,
    p_busca: f.busca?.trim() ? f.busca.trim() : null,
    p_limite: f.limite ?? 50,
    p_offset: f.offset ?? 0,
    p_de: f.de ?? null,
    p_ate: f.ate ?? null,
    p_prazo: f.prazo ?? null,
  });
  if (error) throw error;
  return (data ?? []) as MatriculaDoAdmin[];
}

// ─────────────────────────────────────────────────────────────────────────────
// A planilha
// ─────────────────────────────────────────────────────────────────────────────

/** Quantas linhas cada ida busca ao montar o arquivo. É o teto da RPC. */
const LOTE_DA_PLANILHA = 1000;

/**
 * Teto de linhas por exportação.
 *
 * Não é limite técnico — é um freio: 43.077 matrículas dariam 44 idas ao servidor
 * e um arquivo de vários megabytes montado na memória do navegador. O caso real
 * do pedido ("exportar planilha") é uma turma ou um curso, não a base inteira, e
 * a tela DIZ quando o filtro passa daqui em vez de travar o botão em silêncio.
 */
export const TETO_DA_PLANILHA = 20000;

/**
 * Monta o CSV das matrículas que o filtro alcança, paginando pela MESMA função
 * que a tela usa.
 *
 * ---------------------------------------------------------------------------
 * CSV, e não .xlsx
 * ---------------------------------------------------------------------------
 * "Exportar planilha" foi o pedido, e planilha é o que o Excel abre. Gerar
 * `.xlsx` de verdade exigiria `exceljs` — **939 KB, 270 KB comprimido**, para um
 * botão que a secretaria usa algumas vezes por mês. O hub inteiro é servido em um
 * bundle; pagar isso em toda visita de aluno para um recurso de operador é o
 * mesmo raciocínio que fez o currículo virar impressão do navegador em vez de
 * html2canvas + jsPDF.
 *
 * O que faz o CSV abrir certo no Excel em português:
 *   · **BOM UTF-8** (`﻿`) no começo — sem ele o Excel lê como ANSI e todo
 *     acento vira `Ã§`. É a razão nº 1 de "a planilha veio com caracteres
 *     estranhos".
 *   · **ponto e vírgula** como separador — é o que o Excel pt-BR espera, porque
 *     a vírgula é o separador DECIMAL aqui.
 *   · a linha `sep=;` antes do cabeçalho, que o Excel lê como instrução.
 *
 * ---------------------------------------------------------------------------
 * O filtro é o do SERVIDOR, e essa é a decisão que importa
 * ---------------------------------------------------------------------------
 * Esta função chama `pos_admin_matriculas` com os mesmos parâmetros da tela, em
 * páginas de 1.000. Reescrever o filtro aqui criaria a segunda definição de "o
 * que está sendo exportado" — e o operador confiaria num arquivo que não é o que
 * ele vê na tela. É a mesma razão pela qual o boletim não recalcula "aprovado".
 */
export async function montarCsvDeMatriculas(
  f: Parameters<typeof lerMatriculas>[0],
  aoAndar?: (lidas: number, total: number) => void,
): Promise<{ csv: string; linhas: number; total: number; truncado: boolean }> {
  const linhas: MatriculaDoAdmin[] = [];
  let total = 0;
  let offset = 0;

  for (;;) {
    const lote = await lerMatriculas({ ...f, limite: LOTE_DA_PLANILHA, offset });
    if (lote.length === 0) break;
    total = lote[0].total_no_filtro;
    linhas.push(...lote);
    aoAndar?.(linhas.length, Math.min(total, TETO_DA_PLANILHA));
    offset += lote.length;
    if (offset >= total || linhas.length >= TETO_DA_PLANILHA) break;
  }

  const cabecalho = [
    "Aluno",
    "E-mail",
    "CPF",
    "Curso",
    "Turma",
    "Situação",
    "Instituição",
    "Início",
    "Término previsto",
    "Meses desde o início",
    "Disciplinas na grade",
    "Aprovadas",
    // 000206: a planilha leva a MESMA gaveta que a tela e o filtro. Sem ela, a
    // operadora exporta o resultado de "fora do prazo" e a coluna que explica o
    // recorte não vai junto.
    "Prazo do curso",
  ];

  const corpo = linhas.map((m) => [
    m.aluno_nome ?? "",
    m.aluno_email,
    m.aluno_cpf ?? "",
    m.curso_nome ?? m.curso_slug,
    m.turma_numero !== null ? `Turma ${m.turma_numero}` : "",
    m.situacao,
    // "não informada" e não vazio: célula vazia numa planilha é lida como erro de
    // exportação, e o terceiro estado da instituição é um FATO — 336 pedidos da
    // origem não casam com matrícula nenhuma, e tratá-los como FBC por omissão é
    // o que a 000137 recusou fazer.
    m.instituicao === "focus" ? "Focus" : m.instituicao === "fbc" ? "FBC" : "não informada",
    dataBr(m.inicio),
    dataBr(m.previsao_termino),
    m.meses_desde_o_inicio ?? "",
    m.disciplinas_da_turma,
    m.aprovadas,
    ROTULO_GAVETA_DE_PRAZO[m.prazo] ?? m.prazo,
  ]);

  // O escape de cada célula, o BOM e o `sep=;` moram em `lib/csv.ts` desde
  // 29/09, quando o painel das avaliações passou a exportar também.
  const csv = montarCsv(cabecalho, corpo);

  return {
    csv,
    linhas: linhas.length,
    total,
    truncado: total > linhas.length,
  };
}

function dataBr(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso.length <= 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("pt-BR");
}

/** Reexportado: as telas da pós importam daqui desde 09/09. */
export { baixarCsv };

// ═══════════════════════════════════════════════════════════════════════════
//  As três telas que a secretaria pediu (000224)
// ═══════════════════════════════════════════════════════════════════════════
//
// ZERO regra aqui também: quem valida a disciplina, calcula quem está travado,
// decide se a remoção alcança aluno e resolve a precedência da turma atual é
// o BANCO. Este bloco tipa e chama.

export type TurmaDoAdmin = {
  turma_id: string;
  curso_slug: string;
  curso_nome: string | null;
  numero: number;
  disciplinas: number;
  matriculas: number;
  /**
   * A resposta REAL de `pos_turma_corrente` — não uma segunda derivação na
   * tela. O nome do CAMPO é o do produto ("turma atual"); o nome da função é
   * de 000128 e ficou, porque `conceder_acesso` a chama (000227).
   */
  atual: boolean;
  /** `true` quando alguém da equipe ESCOLHEU esta turma; `false` = derivada. */
  definida_a_mao: boolean;
  /**
   * (000316) O instante a partir do qual as matrículas novas vão para esta
   * turma — nulo quando não há horário marcado. As duas formas escritas vêm
   * PRONTAS do banco, na hora de São Paulo: `new Date` num fuso qualquer é como
   * a data da colação saía um dia antes.
   */
  recebe_matriculas_desde: string | null;
  /** O valor do campo `datetime-local` ("2026-10-01T00:00"), na hora de São Paulo. */
  recebe_matriculas_desde_local: string | null;
  /** A frase ("01/10/2026 às 00:00"), na hora de São Paulo. */
  recebe_matriculas_desde_texto: string | null;
  ja_comecou: boolean | null;
  /**
   * A PRÓXIMA virada do curso — que é o ENCERRAMENTO da turma atual, derivado
   * pelo banco e não guardado. O mesmo valor em todas as turmas do curso.
   */
  proxima_virada_texto: string | null;
  proxima_virada_numero: number | null;
};

export async function lerTurmasDoAdmin(curso?: string | null): Promise<TurmaDoAdmin[] | null> {
  const { data, error } = await supabase.rpc("pos_admin_turmas", { p_curso: curso ?? null });
  if (error) return null;
  return (data ?? []) as TurmaDoAdmin[];
}

export type RespostaDoInicio = {
  ok: boolean;
  motivo?: string;
  /** A frase pronta: o que o ato fez, e se a turma atual mudou por causa dele. */
  detalhe?: string;
  numero?: number;
  atual_numero?: number | null;
  sem_mudanca?: boolean;
  /** Aplicado COM divergência conhecida (princípio 2): turma sem grade, horário que não valeu, ordem trocada. */
  aviso?: string | null;
};

/**
 * Marca (ou, com `null`, tira) o instante em que a turma começa a receber as
 * matrículas novas (000316). `desdeLocal` é o valor do campo `datetime-local`
 * — "2026-10-01T00:00" — e o fuso de São Paulo é aplicado NO BANCO, que é quem
 * sabe o que essa hora é em UTC. A tela não converte hora nenhuma.
 */
export async function agendarTurma(
  turma: string,
  desdeLocal: string | null,
  motivo?: string,
): Promise<RespostaDoInicio> {
  const { data, error } = await supabase.rpc("pos_admin_agendar_turma", {
    p_turma: turma,
    p_desde: desdeLocal,
    p_motivo: motivo ?? null,
  });
  if (error) return { ok: false, motivo: "erro", detalhe: error.message };
  return (data ?? { ok: false }) as RespostaDoInicio;
}

export type RespostaDaTurmaCorrente = {
  ok: boolean;
  motivo?: string;
  detalhe?: string;
  numero?: number;
  disciplinas?: number;
  derivando?: boolean;
  /**
   * O terceiro estado do princípio 2: APLICADO, com divergência conhecida. Vem
   * preenchido quando a turma escolhida não tem grade — o que é aceito de
   * propósito (é o caso de abrir a turma nova antes de montar a grade) e nunca
   * em silêncio.
   */
  aviso?: string | null;
};

/**
 * Define (ou desfaz) a turma ATUAL do curso.
 *
 * O nome da RPC continua `pos_admin_definir_turma_corrente`: ela nasceu na
 * 000224 e o termo do banco vem de 000128, onde `pos_turma_corrente` é chamada
 * por `conceder_acesso`. O front fala o vocabulário do PRODUTO; a fronteira
 * carrega o nome que o banco tem. A correspondência está no Vocabulário do
 * CLAUDE.md.
 */
export async function definirTurmaAtual(
  curso: string,
  turma: string | null,
  motivo?: string,
): Promise<RespostaDaTurmaCorrente> {
  const { data, error } = await supabase.rpc("pos_admin_definir_turma_corrente", {
    p_curso: curso,
    p_turma: turma,
    p_motivo: motivo ?? null,
  });
  if (error) return { ok: false, motivo: "erro", detalhe: error.message };
  return (data ?? { ok: false }) as RespostaDaTurmaCorrente;
}

export type DisciplinaNaGrade = {
  disciplina_id: string;
  nome: string;
  carga_horaria_horas: number | null;
  docente_nome: string | null;
  ordem: number;
  /**
   * Quantos alunos DESTA turma já fizeram prova nesta disciplina. É o número que
   * a tela mostra antes de deixar remover: sem ele, "remover" parece operação de
   * catálogo e some com a linha do boletim de quem já prestou a avaliação.
   */
  alunos_com_tentativa: number;
};

export type DisciplinaDisponivel = {
  disciplina_id: string;
  nome: string;
  carga_horaria_horas: number | null;
  docente_nome: string | null;
  /** Menos de 10 desabilita a prova — a tela avisa antes de a disciplina entrar. */
  questoes_ativas: number;
};

export type GradeDaTurma = {
  ok: boolean;
  motivo?: string;
  turma_id: string;
  curso_slug: string;
  numero: number;
  na_grade: DisciplinaNaGrade[];
  disponiveis: DisciplinaDisponivel[];
};

export async function lerGradeDaTurma(turma: string): Promise<GradeDaTurma | null> {
  const { data, error } = await supabase.rpc("pos_admin_grade", { p_turma: turma });
  if (error) return null;
  return (data ?? null) as GradeDaTurma | null;
}

export type RespostaDaGrade = {
  ok: boolean;
  motivo?: string;
  detalhe?: string;
  total?: number;
  saindo?: number;
  entrando?: number;
  alunos_afetados?: number;
  afetados?: { disciplina_id: string; nome: string; alunos: number }[];
  aviso?: string | null;
  /** (000317) A versão da grade que o ato gravou — ou a vigente, quando nada mudou. */
  versao?: number | null;
  /** (000317) A mesma lista: não é alteração, não pede motivo e não grava versão. */
  sem_mudanca?: boolean;
  /** (000317) Na recusa da remoção que alcança aluno: confirmar também vai pedir o porquê. */
  pede_motivo?: boolean;
};

/**
 * Manda a lista COMPLETA na ordem desejada — nunca "acrescente X" / "remova Y".
 *
 * Duas razões, e as duas são do banco: a ordem é atributo do conjunto (reordenar
 * seria N chamadas com estados intermediários inválidos), e lista completa torna
 * a operação idempotente — reenviar a mesma não faz trabalho nenhum.
 *
 * `confirmar` é o segundo ato quando a remoção alcança aluno que já fez prova. A
 * primeira chamada devolve `ok:false` com `motivo:'remocao_afeta_alunos'` e a
 * lista de quem seria alcançado; é a tela que decide mostrar e perguntar.
 *
 * `motivo` (000317): ALTERAR uma grade que existe pede o porquê, e a grade de
 * antes fica guardada como versão. Quem decide se ele é obrigatório é o banco —
 * a primeira grade de uma turma vazia não pede.
 */
export async function definirGrade(
  turma: string,
  disciplinas: string[],
  confirmar = false,
  motivo?: string,
): Promise<RespostaDaGrade> {
  const { data, error } = await supabase.rpc("pos_admin_definir_grade", {
    p_turma: turma,
    p_disciplinas: disciplinas,
    p_confirmar: confirmar,
    p_motivo: motivo ?? null,
  });
  if (error) return { ok: false, motivo: "erro", detalhe: error.message };
  return (data ?? { ok: false }) as RespostaDaGrade;
}

export type VersaoDaGrade = {
  versao: number;
  vigente: boolean;
  origem: "inicial" | "edicao" | "avaliacao_nova";
  motivo: string | null;
  por: string;
  /** Pronta do banco, na hora de São Paulo. */
  em_texto: string;
  total_disciplinas: number;
  total_horas: number;
  /** A FOTOGRAFIA: nome, carga e docente como estavam quando a versão foi gravada. */
  disciplinas: {
    disciplina_id: string;
    nome: string;
    carga_horaria_horas: number | null;
    docente_nome: string | null;
    docente_titulacao: string | null;
    posicao: number;
  }[];
};

/** As versões da grade de uma turma, da mais nova para a mais antiga (000317). */
export async function lerVersoesDaGrade(turma: string): Promise<VersaoDaGrade[] | null> {
  const { data, error } = await supabase.rpc("pos_admin_versoes_da_grade", { p_turma: turma });
  if (error) return null;
  return ((data as { versoes?: VersaoDaGrade[] } | null)?.versoes ?? []) as VersaoDaGrade[];
}

export type AlunoTravado = {
  user_id: string;
  aluno_nome: string | null;
  aluno_email: string | null;
  disciplina_id: string;
  disciplina: string;
  curso_slug: string;
  tentativas: number;
  melhor_nota: number | null;
  restantes: number;
  ultima_em: string | null;
};

/**
 * Quem esgotou as tentativas E não passou.
 *
 * As duas condições juntas, medidas em 14/09: 2.804 pares esgotaram as cinco, e
 * só **29** estão sem aprovação. Uma lista com 99% de gente que já passou é uma
 * lista que ninguém usa.
 */
export async function lerTravados(
  curso?: string | null,
  busca?: string,
  turma?: string | null,
): Promise<AlunoTravado[] | null> {
  const { data, error } = await supabase.rpc("pos_admin_travados", {
    p_curso: curso ?? null,
    p_busca: busca ?? null,
    p_turma: turma ?? null,
  });
  if (error) return null;
  return (data ?? []) as AlunoTravado[];
}

export type RespostaDaLiberacao = {
  ok: boolean;
  motivo?: string;
  detalhe?: string;
  liberadas?: number;
  /** O ESTADO NOVO. A pergunta de quem opera é "o aluno já pode fazer a prova?". */
  restantes?: number;
};

export async function liberarTentativas(
  user: string,
  disciplina: string,
  extras: number,
  motivo: string,
): Promise<RespostaDaLiberacao> {
  const { data, error } = await supabase.rpc("pos_admin_liberar_tentativas", {
    p_user: user,
    p_disciplina: disciplina,
    p_extras: extras,
    p_motivo: motivo,
  });
  if (error) return { ok: false, motivo: "erro", detalhe: error.message };
  return (data ?? { ok: false }) as RespostaDaLiberacao;
}

// ─────────────────────────────────────────────────────────────────────────────
// A CORREÇÃO de nota (000243)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * O que a correção devolve. `mudou` separa "corrigi" de "já era esse valor" —
 * cliques repetidos não viram registro de auditoria, e a tela não deve dizer
 * "corrigido" quando nada mudou.
 *
 * `melhor_nota` e `aprovado` são o ESTADO NOVO do aluno, não o eco do que foi
 * digitado: a aprovação é pela MAIOR nota, então corrigir uma tentativa para 6
 * não aprova quem tem outra com 4 — e não REPROVA quem tem outra com 9. É a
 * mesma lição da liberação de tentativas, onde a resposta diz quantas o aluno
 * tem agora em vez de "gravei".
 */
export type RespostaDaCorrecao =
  | {
      ok: true;
      mudou: boolean;
      disciplina?: string;
      nota_antes?: number | null;
      nota?: number;
      melhor_nota?: number | null;
      aprovado?: boolean;
      nota_minima?: number;
      detalhe?: string;
    }
  | { ok: false; motivo: string; detalhe?: string };

export async function editarNota(
  tentativa: string,
  nota: number,
  motivo: string,
): Promise<RespostaDaCorrecao> {
  const { data, error } = await supabase.rpc("pos_admin_editar_nota", {
    p_tentativa: tentativa,
    p_nota: nota,
    p_motivo: motivo,
  });
  if (error) return { ok: false, motivo: "erro", detalhe: error.message };
  return (data ?? { ok: false, motivo: "sem_resposta" }) as RespostaDaCorrecao;
}

/** Uma correção já feita, como a auditoria a guarda. */
export type CorrecaoDeNota = {
  seq: number;
  tentativa_id: string;
  aluno: string | null;
  disciplina: string | null;
  nota_antes: number | null;
  nota_depois: number;
  motivo: string;
  editado_por: string | null;
  criado_em: string;
};

/**
 * O histórico de correções de UMA tentativa (ou de um aluno).
 *
 * A RPC exige recorte — chamada sem nenhum ela recusa com `sem_recorte`, em vez
 * de devolver a auditoria inteira. Aqui o recorte é sempre a tentativa.
 */
export async function lerCorrecoesDeNota(tentativa: string): Promise<CorrecaoDeNota[]> {
  const { data, error } = await supabase.rpc("pos_admin_correcoes_de_nota", {
    p_tentativa: tentativa,
    p_user: null,
    p_limite: 50,
  });
  if (error) return [];
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.ok !== true) return [];
  return (r.correcoes ?? []) as CorrecaoDeNota[];
}

export type LiberacaoRegistrada = {
  id: string;
  aluno_nome: string | null;
  aluno_email: string | null;
  disciplina: string;
  curso_slug: string;
  tentativas_extras: number;
  motivo: string | null;
  liberado_por: string | null;
  criado_em: string;
};

export async function lerLiberacoes(user?: string | null): Promise<LiberacaoRegistrada[] | null> {
  const { data, error } = await supabase.rpc("pos_admin_liberacoes", {
    p_user: user ?? null,
    p_limite: 100,
  });
  if (error) return null;
  return (data ?? []) as LiberacaoRegistrada[];
}

// ═══════════════════════════════════════════════════════════════════════════
//  Criar turma e criar avaliação (000228)
// ═══════════════════════════════════════════════════════════════════════════

export type RespostaDeCriacao = {
  ok: boolean;
  motivo?: string;
  detalhe?: string;
  turma_id?: string;
  numero?: number;
  disciplina_id?: string;
  nome?: string;
  na_grade?: boolean;
  /**
   * O terceiro estado do princípio 2. Aqui ele tem dois papéis diferentes:
   * na turma, avisa que ela nasce sem grade e por isso não recebe matrícula;
   * na avaliação, que a prova é de 10 questões e ela ainda não tem nenhuma.
   */
  aviso?: string | null;
};

/**
 * Cria a turma. Sem número, o banco usa **max + 1** — e não `count + 1`.
 *
 * A distinção tem caso real: `pos-rotinas-simples` tem as turmas 1, 2, 3 e **7**
 * (as 4, 5 e 6 não existem, é dado da origem), e `count + 1` daria 5.
 */
export async function criarTurma(curso: string, numero?: number | null): Promise<RespostaDeCriacao> {
  const { data, error } = await supabase.rpc("pos_admin_criar_turma", {
    p_curso: curso,
    p_numero: numero ?? null,
  });
  if (error) return { ok: false, motivo: "erro", detalhe: error.message };
  return (data ?? { ok: false }) as RespostaDeCriacao;
}

/**
 * Cria a avaliação (disciplina) e, quando vem turma, já a põe na grade dela.
 *
 * Os dois atos juntos de propósito: criar e depois ter de ir ao editor pôr na
 * grade seriam dois passos para um pedido só, e quem esquecesse o segundo teria
 * criado uma avaliação que ninguém cursa.
 */
export async function criarAvaliacao(dados: {
  curso: string;
  nome: string;
  carga?: number | null;
  docente?: string | null;
  titulacao?: string | null;
  turma?: string | null;
}): Promise<RespostaDeCriacao> {
  const { data, error } = await supabase.rpc("pos_admin_criar_disciplina", {
    p_curso: dados.curso,
    p_nome: dados.nome,
    p_carga_horaria: dados.carga ?? null,
    p_docente: dados.docente ?? null,
    p_docente_titulacao: dados.titulacao ?? null,
    p_turma: dados.turma ?? null,
  });
  if (error) return { ok: false, motivo: "erro", detalhe: error.message };
  return (data ?? { ok: false }) as RespostaDeCriacao;
}

export type AlternativaDaQuestao = {
  letra: string;
  conteudo: string;
  correta: boolean;
};

export type QuestaoDaAvaliacao = {
  questao_id: string;
  ordem: number | null;
  ativa: boolean;
  texto: string | null;
  /** Já foi respondida por alguém: editar o enunciado muda a prova de quem a fez. */
  usada_em_tentativas: boolean;
  alternativas: AlternativaDaQuestao[];
};

export type QuestoesDaAvaliacao = {
  ok: boolean;
  motivo?: string;
  disciplina_id: string;
  nome: string;
  curso_slug: string;
  ativas: number;
  /**
   * Calculado pelo BANCO, com o MESMO número de `pos_iniciar_tentativa`. Uma
   * segunda definição de "a prova abre?" na tela divergiria no dia em que o
   * mínimo mudasse.
   */
  abre_prova: boolean;
  minimo: number;
  questoes: QuestaoDaAvaliacao[];
};

export async function lerQuestoes(disciplina: string): Promise<QuestoesDaAvaliacao | null> {
  const { data, error } = await supabase.rpc("pos_admin_questoes", { p_disciplina: disciplina });
  if (error) return null;
  return (data ?? null) as QuestoesDaAvaliacao | null;
}

export type RespostaDaQuestao = {
  ok: boolean;
  motivo?: string;
  detalhe?: string;
  questao_id?: string;
  ativas?: number;
  abre_prova?: boolean;
  /** Quantas ainda faltam para a prova abrir — zero quando já abre. */
  faltam?: number;
  aviso?: string | null;
};

export async function salvarQuestao(
  disciplina: string,
  texto: string,
  alternativas: AlternativaDaQuestao[],
  questao?: string | null,
): Promise<RespostaDaQuestao> {
  const { data, error } = await supabase.rpc("pos_admin_salvar_questao", {
    p_disciplina: disciplina,
    p_texto: texto,
    p_alternativas: alternativas,
    p_questao: questao ?? null,
  });
  if (error) return { ok: false, motivo: "erro", detalhe: error.message };
  return (data ?? { ok: false }) as RespostaDaQuestao;
}

export async function ativarQuestao(questao: string, ativa: boolean): Promise<RespostaDaQuestao> {
  const { data, error } = await supabase.rpc("pos_admin_ativar_questao", {
    p_questao: questao,
    p_ativa: ativa,
  });
  if (error) return { ok: false, motivo: "erro", detalhe: error.message };
  return (data ?? { ok: false }) as RespostaDaQuestao;
}

/**
 * Exclui a turma — sem tocar nas avaliações.
 *
 * A ressalva de Victor tem número: **185 disciplinas** estão em mais de uma
 * turma, e "Metodologia Científica" está em **37**. Apagar a disciplina junto
 * destruiria a grade de 36 outras. O que sai é o VÍNCULO (`pos_grade`), e isso é
 * a FK em cascata — a disciplina fica no curso e nas outras turmas.
 *
 * Duas chamadas, de propósito: sem `confirmar`, a função devolve
 * `motivo: "confirme"` com o impacto (quantas disciplinas saem da grade, se ela
 * é a turma atual) e NÃO apaga nada. É o que a tela põe no popup.
 *
 * Turma com matrícula é recusada antes disso, com o número de alunos — e a
 * recusa é o caso comum: das 59 turmas, só 2 não têm matrícula.
 *
 * Desde a 000299, confirmar pede o MOTIVO: sem ele a resposta é
 * `motivo_obrigatorio` e nada é apagado. O razão guarda a turma e a grade
 * inteira que saíram — é o que deixa a exclusão reconstruível à mão.
 */
export async function excluirTurma(
  turma: string,
  confirmar = false,
  motivo?: string | null,
): Promise<RespostaDaExclusao> {
  const { data, error } = await supabase.rpc("pos_admin_excluir_turma", {
    p_turma: turma,
    p_confirmar: confirmar,
    p_motivo: motivo ?? null,
  });
  if (error) return { ok: false, motivo: "erro", detalhe: error.message };
  return (data ?? { ok: false }) as RespostaDaExclusao;
}

export type RespostaDaExclusao = {
  ok: boolean;
  motivo?:
    | "confirme"
    | "turma_com_matricula"
    | "turma_inexistente"
    | "motivo_obrigatorio"
    | "erro"
    | string;
  detalhe?: string;
  numero?: number;
  curso_slug?: string;
  matriculas?: number;
  disciplinas?: number;
  era_atual?: boolean;
  /** O ensaio avisa que confirmar vai pedir o porquê (000299). */
  pede_motivo?: boolean;
  disciplinas_desvinculadas?: number;
  aviso?: string | null;
};

/** Uma linha do registro das turmas de um curso (000299). */
export type MudancaDaTurma = {
  id: number;
  em: string;
  acao: string;
  /** A frase do BANCO — a tela mostra, não escreve. */
  resumo: string;
  por: string;
  motivo: string | null;
};

/**
 * O que aconteceu com as turmas de um curso: criação, exclusão (com o motivo)
 * e cronograma, lidos do razão. Zero regra aqui — o recorte por curso é do
 * banco, e a exclusão guarda o curso no corpo da linha porque depois dela a
 * turma não existe para ser juntada.
 */
export async function lerRegistroDasTurmas(
  curso: string,
): Promise<{ ok: boolean; total?: number; mudancas?: MudancaDaTurma[]; detalhe?: string }> {
  const { data, error } = await supabase.rpc("pos_admin_registro_das_turmas", { p_curso: curso });
  if (error) return { ok: false, detalhe: error.message };
  return (data ?? { ok: false }) as { ok: boolean; total?: number; mudancas?: MudancaDaTurma[] };
}

// ---------------------------------------------------------------------------
// O CADASTRO de docentes e disciplinas (000244-000247, 000275)
// ---------------------------------------------------------------------------
// Zero regra aqui, como no resto deste arquivo: quem valida o e-mail, o
// telefone, o nome repetido e a carga horária é o BANCO, e a tela mostra a
// mensagem que ele devolve. Repetir a validação aqui criaria a segunda definição
// da mesma regra, e é ela que divergiria em silêncio.
//
// Do DOCENTE só resta a LEITURA (`lerDocentes`, que a tela de disciplinas usa
// no combo). A escrita saiu daqui na 000275: o cadastro de professor mora na
// Central de Professores, e a tela escreve lá por `lib/professoresDaCentral.ts`.
// `pos_docentes` é PROJEÇÃO — não há RPC de escrita dela para `authenticated`,
// e o nível não se escolhe: é derivado das formações registradas na Central.

export type NivelDoDocente = "especialista" | "mestre" | "doutor";

export type DocenteDoAdmin = {
  id: string;
  nome: string;
  nivel: NivelDoDocente;
  nivel_texto: string;
  email: string | null;
  telefone: string | null;
  observacao: string | null;
  ativo: boolean;
  /** Quantas disciplinas ele tem. É o número que diz se desativá-lo é inócuo. */
  disciplinas: number;
  /** Em que cursos — 11 dos 47 dão aula em mais de um. */
  cursos: string[];
};

export type ListaDeDocentes = {
  ok: boolean;
  docentes: DocenteDoAdmin[];
  total: number;
  inativos: number;
};

export async function lerDocentes(f?: {
  busca?: string | null;
  incluirInativos?: boolean;
}): Promise<ListaDeDocentes | null> {
  const { data, error } = await supabase.rpc("pos_admin_docentes", {
    p_busca: f?.busca ?? null,
    p_incluir_inativos: f?.incluirInativos ?? false,
  });
  if (error) return null;
  return (data ?? null) as ListaDeDocentes | null;
}

export type RespostaDeAtivacao = {
  ok: boolean;
  motivo?: string;
  detalhe?: string;
  ativo?: boolean;
  ativa?: boolean;
  disciplinas?: number;
  turmas?: number;
};

export type DisciplinaDoAdmin = {
  id: string;
  curso_slug: string;
  nome: string;
  carga_horaria_horas: number | null;
  ordem: number | null;
  ativa: boolean;
  docente_id: string | null;
  docente_nome: string | null;
  docente_titulacao: string | null;
  /** Os três números que dizem se mexer nela alcança alguém. */
  questoes_ativas: number;
  tentativas: number;
  turmas: number;
};

export type ListaDeDisciplinas = {
  ok: boolean;
  disciplinas: DisciplinaDoAdmin[];
  total: number;
  inativas: number;
  sem_docente: number;
};

export async function lerDisciplinasDoAdmin(f?: {
  curso?: string | null;
  busca?: string | null;
  incluirInativas?: boolean;
}): Promise<ListaDeDisciplinas | null> {
  const { data, error } = await supabase.rpc("pos_admin_disciplinas", {
    p_curso: f?.curso ?? null,
    p_busca: f?.busca ?? null,
    p_incluir_inativas: f?.incluirInativas ?? false,
  });
  if (error) return null;
  return (data ?? null) as ListaDeDisciplinas | null;
}

export type RespostaDaDisciplina = {
  ok: boolean;
  motivo?: string;
  detalhe?: string;
  disciplina_id?: string;
  criada?: boolean;
  /** O terceiro estado do princípio 2: criada, e a prova dela ainda não funciona. */
  aviso?: string | null;
};

export async function salvarDisciplina(d: {
  id?: string | null;
  curso: string;
  nome: string;
  cargaHoraria?: number | null;
  docenteId?: string | null;
}): Promise<RespostaDaDisciplina> {
  const { data, error } = await supabase.rpc("pos_admin_salvar_disciplina", {
    p_curso: d.curso,
    p_nome: d.nome,
    p_carga_horaria: d.cargaHoraria ?? null,
    p_docente: d.docenteId ?? null,
    p_disciplina: d.id ?? null,
  });
  if (error) return { ok: false, motivo: "erro", detalhe: error.message };
  return (data ?? { ok: false }) as RespostaDaDisciplina;
}

export async function ativarDisciplina(id: string, ativa: boolean): Promise<RespostaDeAtivacao> {
  const { data, error } = await supabase.rpc("pos_admin_ativar_disciplina", {
    p_disciplina: id,
    p_ativa: ativa,
  });
  if (error) return { ok: false, motivo: "erro", detalhe: error.message };
  return (data ?? { ok: false }) as RespostaDeAtivacao;
}

// ---------------------------------------------------------------------------
// O CRONOGRAMA DE LIBERAÇÃO (000272)
// ---------------------------------------------------------------------------
// Zero regra aqui, como no resto deste arquivo. A DATA de liberação não é
// calculada no front: ela vem derivada do banco (`inicio_em + prazo_dias`), e
// recalculá-la aqui criaria a segunda definição de "quando esta disciplina
// abre" — o defeito de agregar acesso em JavaScript com outra roupa.

export type ItemDoCronograma = {
  disciplina_id: string;
  nome: string;
  modulo: number | null;
  prazo_dias: number | null;
  ordem_grade: number;
  /** DERIVADA no banco. Nula quando a turma não tem início ou a disciplina não tem prazo. */
  libera_em: string | null;
  /** Nula pela mesma razão — e nulo aqui é "não dá para saber", nunca "não liberada". */
  liberada: boolean | null;
};

export type ResumoDoCronograma = {
  total: number;
  com_prazo: number;
  sem_prazo: number;
  liberadas: number;
  faltam: number;
  proxima_em: string | null;
  /**
   * A ordem da grade discorda da ordem de liberação? O banco DIZ e não conserta:
   * sobrescrever uma pela outra decidiria em silêncio qual está certa, e a da
   * grade é a que o aluno vê hoje.
   */
  ordem_diverge_da_grade: boolean;
};

export type CronogramaDaTurma = {
  ok: boolean;
  motivo?: string;
  detalhe?: string;
  curso_slug?: string;
  turma?: { id: string; numero: number; inicio_em: string | null };
  itens?: ItemDoCronograma[];
  resumo?: ResumoDoCronograma;
};

export async function lerCronograma(
  curso: string,
  turma?: string | null,
): Promise<CronogramaDaTurma | null> {
  const { data, error } = await supabase.rpc("pos_admin_cronograma", {
    p_curso: curso,
    p_turma: turma ?? null,
  });
  if (error) return null;
  return (data ?? null) as CronogramaDaTurma | null;
}

export type RespostaDoCronograma = {
  ok: boolean;
  motivo?: string;
  detalhe?: string;
  mudariam?: number;
  alunos?: number;
  inicio_de?: string | null;
  inicio_para?: string | null;
};

/**
 * Manda o cronograma COMPLETO da turma, como `definirGrade` manda a grade
 * completa — e pela mesma razão: o conjunto é a unidade, e um lote completo é
 * idempotente.
 *
 * `confirmar = false` é ENSAIO: o banco diz o que faria e não escreve nada. A
 * tela sempre ensaia antes, porque a data que sai daqui é a que a equipe vai
 * anunciar ao aluno.
 */
export async function salvarCronograma(
  turma: string,
  inicio: string | null,
  itens: { disciplina_id: string; prazo_dias: number | null; modulo: number | null }[],
  motivo: string,
  confirmar: boolean,
): Promise<RespostaDoCronograma> {
  const { data, error } = await supabase.rpc("pos_admin_salvar_cronograma", {
    p_turma: turma,
    p_inicio: inicio,
    p_itens: itens,
    p_motivo: motivo,
    p_confirmar: confirmar,
  });
  if (error) return { ok: false, motivo: "erro", detalhe: error.message };
  return (data ?? { ok: false }) as RespostaDoCronograma;
}
